import { TKHQ } from "./turnkey-core.js";
import {
  recordChannelTelemetry,
  CHANNEL_MESSAGE_CHANNEL,
  CHANNEL_LEGACY_POST_MESSAGE,
} from "./telemetry.js";
import {
  getCompiledTransactionMessageDecoder,
  getCompiledTransactionMessageEncoder,
} from "@solana/transaction-messages";
import {
  getTransactionDecoder,
  getTransactionEncoder,
} from "@solana/transactions";
import { parseTransaction } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import * as nobleEd25519 from "@noble/ed25519";
import * as nobleHashes from "@noble/hashes/sha512";

// Set up sha512 for nobleEd25519 (required for signing and key derivation)
nobleEd25519.etc.sha512Sync = (...m) =>
  nobleHashes.sha512(nobleEd25519.etc.concatBytes(...m));

// Persist keys in memory via mapping of { address --> pk }
let inMemoryKeys = {};

// Injected embedded key -- held in memory only, never persisted.
// When set, decryptBundle uses this P-256 JWK instead of the iframe's embedded key.
let injectedEmbeddedKey = null;

export const DEFAULT_TTL_MILLISECONDS = 1000 * 24 * 60 * 60; // 24 hours or 86,400,000 milliseconds

// Instantiate these once (for perf)
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/**
 * Verifies the enclave signature on a v1.0.0 bundle and returns the parsed signed data.
 * @param {string} bundle - JSON-stringified bundle
 * @param {string} organizationId - Expected organization ID
 * @returns {Promise<Object>} - The parsed signed data {organizationId, encappedPublic, ciphertext}
 */
async function verifyAndParseBundleData(bundle, organizationId) {
  const bundleObj = JSON.parse(bundle);

  if (bundleObj.version !== "v1.0.0") {
    throw new Error(`unsupported version: ${bundleObj.version}`);
  }
  if (!bundleObj.data) {
    throw new Error('missing "data" in bundle');
  }
  if (!bundleObj.dataSignature) {
    throw new Error('missing "dataSignature" in bundle');
  }
  if (!bundleObj.enclaveQuorumPublic) {
    throw new Error('missing "enclaveQuorumPublic" in bundle');
  }

  if (!TKHQ.verifyEnclaveSignature) {
    throw new Error("method TKHQ.verifyEnclaveSignature not loaded");
  }
  const verified = await TKHQ.verifyEnclaveSignature(
    bundleObj.enclaveQuorumPublic,
    bundleObj.dataSignature,
    bundleObj.data
  );
  if (!verified) {
    throw new Error(
      `failed to verify enclave signature. Got signature: ${bundleObj.dataSignature}, enclaveQuorumPublic: ${bundleObj.enclaveQuorumPublic}`
    );
  }

  const signedData = JSON.parse(
    textDecoder.decode(TKHQ.uint8arrayFromHexString(bundleObj.data))
  );

  if (!organizationId) {
    throw new Error(
      `organization id is required. Please ensure you are using @turnkey/iframe-stamper >= v2.0.0 to pass "organizationId" for security purposes.`
    );
  } else if (
    !signedData.organizationId ||
    signedData.organizationId !== organizationId
  ) {
    throw new Error(
      `organization id does not match expected value. Expected: ${organizationId}. Found: ${signedData.organizationId}.`
    );
  }

  if (!signedData.encappedPublic) {
    throw new Error('missing "encappedPublic" in bundle signed data');
  }
  if (!signedData.ciphertext) {
    throw new Error('missing "ciphertext" in bundle signed data');
  }

  return signedData;
}

/**
 * Parse and decrypt the export bundle.
 * The `bundle` param is a JSON string of the encapsulated public
 * key, encapsulated public key signature, and the ciphertext.
 * @param {string} bundle
 * @param {string} organizationId
 * @param {Function} HpkeDecrypt
 */
async function decryptBundle(bundle, organizationId, HpkeDecrypt) {
  const signedData = await verifyAndParseBundleData(bundle, organizationId);

  const encappedKeyBuf = TKHQ.uint8arrayFromHexString(
    signedData.encappedPublic
  );
  const ciphertextBuf = TKHQ.uint8arrayFromHexString(signedData.ciphertext);

  // Use the injected embedded key if available, otherwise fall back to the embedded key
  const receiverPrivJwk = injectedEmbeddedKey || (await TKHQ.getEmbeddedKey());
  return await HpkeDecrypt({
    ciphertextBuf,
    encappedKeyBuf,
    receiverPrivJwk,
  });
}

/**
 * Function triggered when GET_EMBEDDED_PUBLIC_KEY event is received.
 * @param {string} requestId
 */
async function onGetPublicEmbeddedKey(requestId) {
  const embeddedKeyJwk = TKHQ.getEmbeddedKey();

  if (!embeddedKeyJwk) {
    TKHQ.sendMessageUp("EMBEDDED_PUBLIC_KEY", "", requestId); // no key == empty string

    return;
  }

  const targetPubBuf = await TKHQ.p256JWKPrivateToPublic(embeddedKeyJwk);
  const targetPubHex = TKHQ.uint8arrayToHexString(targetPubBuf);

  // Send up EMBEDDED_PUBLIC_KEY message
  TKHQ.sendMessageUp("EMBEDDED_PUBLIC_KEY", targetPubHex, requestId);
}

/**
 * Encodes raw key bytes and loads them into the in-memory key store.
 * @param {string} address - Wallet address (case-sensitive)
 * @param {ArrayBuffer} keyBytes - Raw decrypted private key bytes
 * @param {string} keyFormat - "SOLANA" | "HEXADECIMAL"
 * @param {string} organizationId - Organization ID
 */
async function loadKeyIntoMemory(address, keyBytes, keyFormat, organizationId) {
  let key;
  const privateKeyBytes = new Uint8Array(keyBytes);

  if (keyFormat === "SOLANA") {
    const privateKeyHex = TKHQ.uint8arrayToHexString(
      privateKeyBytes.subarray(0, 32)
    );
    const publicKeyBytes = TKHQ.getEd25519PublicKey(privateKeyHex);
    key = await TKHQ.encodeKey(privateKeyBytes, keyFormat, publicKeyBytes);
  } else {
    key = await TKHQ.encodeKey(privateKeyBytes, keyFormat);
  }

  const keyAddress = address || "default";

  // Cache keypair for improved signing perf
  let cachedKeypair;
  if (keyFormat === "SOLANA") {
    cachedKeypair = solanaKeypairFromSecretKey(TKHQ.base58Decode(key));
  } else if (keyFormat === "HEXADECIMAL") {
    cachedKeypair = await createSolanaKeypair(
      Array.from(TKHQ.uint8arrayFromHexString(key))
    );
  }

  inMemoryKeys = {
    ...inMemoryKeys,
    [keyAddress]: {
      organizationId,
      privateKey: key,
      format: keyFormat,
      expiry: new Date().getTime() + DEFAULT_TTL_MILLISECONDS,
      keypair: cachedKeypair,
    },
  };
}

/**
 * Function triggered when INJECT_KEY_EXPORT_BUNDLE event is received.
 * @param {string} requestId
 * @param {string} organizationId
 * @param {string} bundle
 * @param {string} keyFormat
 * @param {string} address
 * @param {Function} HpkeDecrypt // TODO: import this directly (instead of passing around)
 */
async function onInjectKeyBundle(
  requestId,
  organizationId,
  bundle,
  keyFormat,
  address,
  HpkeDecrypt
) {
  // Decrypt the export bundle
  const keyBytes = await decryptBundle(bundle, organizationId, HpkeDecrypt);

  // Load decrypted key into memory
  await loadKeyIntoMemory(address, keyBytes, keyFormat, organizationId);

  // Send up BUNDLE_INJECTED message
  TKHQ.sendMessageUp("BUNDLE_INJECTED", true, requestId);
}

/**
 * Function triggered when APPLY_SETTINGS event is received.
 * For now, the only settings that can be applied are for "styles".
 * Persist them in local storage so they can be applied on every
 * page load.
 * @param {string} settings: JSON-stringified settings
 * @param {string} requestId
 */
async function onApplySettings(settings, requestId) {
  // Apply settings
  const validSettings = TKHQ.applySettings(settings);

  // Persist in local storage
  TKHQ.setSettings(validSettings);

  // Send up SETTINGS_APPLIED message
  TKHQ.sendMessageUp("SETTINGS_APPLIED", true, requestId);
}

/**
 * Function triggered when SIGN_TRANSACTION event is received.
 * @param {string} requestId
 * @param {string} transaction (serialized)
 * @param {string} address (case-sensitive --> enforce this, optional for backwards compatibility)
 */
async function onSignTransaction(requestId, serializedTransaction, address) {
  // If no address provided, use "default"
  const keyAddress = address || "default";
  const key = inMemoryKeys[keyAddress];

  // Validate key exists and is valid/non-expired
  if (!validateKey(key, keyAddress, requestId)) {
    return;
  }

  const transactionWrapper = JSON.parse(serializedTransaction);
  const transactionToSign = transactionWrapper.transaction;
  const transactionType = transactionWrapper.type;

  let signedTransaction;

  if (transactionType === "SOLANA") {
    // Get or create keypair (uses cached keypair if available)
    const keypair = await getOrCreateKeypair(key);

    // Fetch the transaction and sign
    const transactionBytes = TKHQ.uint8arrayFromHexString(transactionToSign);
    const transaction = decodeSolanaTransaction(transactionBytes);
    const signerAddress = TKHQ.base58Encode(keypair.publicKey);
    if (!(signerAddress in transaction.signatures)) {
      throw new Error("key is not a signer for this transaction");
    }
    const signature = nobleEd25519.sign(
      transaction.messageBytes,
      keypair.secretKey.slice(0, 32)
    );

    // Only this key's signature changes; other signatures are kept as-is
    signedTransaction = getTransactionEncoder().encode({
      ...transaction,
      signatures: { ...transaction.signatures, [signerAddress]: signature },
    });
  } else if (transactionType === "ETHEREUM") {
    // viem returns the fully serialized, broadcast-ready signed tx as a
    // 0x-prefixed hex string.
    signedTransaction = await signEthereumTransaction(key, transactionToSign);
  } else {
    throw new Error("unsupported transaction type");
  }

  // Solana signing returns raw bytes (encode to hex); Ethereum signing already
  // returns a 0x-prefixed hex string, which we pass through as-is.
  const signedTransactionHex =
    typeof signedTransaction === "string"
      ? signedTransaction
      : TKHQ.uint8arrayToHexString(signedTransaction);

  TKHQ.sendMessageUp("TRANSACTION_SIGNED", signedTransactionHex, requestId);
}

/**
 * Function triggered when SIGN_MESSAGE event is received.
 * @param {string} requestId
 * @param {string} message (serialized, JSON-stringified)
 * @param {string} address (case-sensitive --> enforce this, optional for backwards compatibility)
 */
async function onSignMessage(requestId, serializedMessage, address) {
  // Backwards compatibility: if no address provided, use "default"
  const keyAddress = address || "default";
  const key = inMemoryKeys[keyAddress];

  // Validate key exists and has not expired
  if (!validateKey(key, keyAddress, requestId)) {
    return;
  }

  const messageWrapper = JSON.parse(serializedMessage);
  const messageToSign = messageWrapper.message;
  const messageType = messageWrapper.type;
  const messageBytes = textEncoder.encode(messageToSign);

  let signatureHex;

  if (messageType === "SOLANA") {
    // Get or create keypair (uses cached keypair if available)
    const keypair = await getOrCreateKeypair(key);

    // Extract the 32-byte private key from the 64-byte secretKey
    // Solana keypair.secretKey format: [32-byte private key][32-byte public key]
    const privateKey = keypair.secretKey.slice(0, 32);
    // Sign the message using nobleEd25519
    const signature = nobleEd25519.sign(messageBytes, privateKey);

    // Note: Signature verification is skipped for performance. The signature will always be valid if signing succeeds with a valid keypair.
    // Clients can verify the signature returned.

    signatureHex = TKHQ.uint8arrayToHexString(signature);
  } else if (messageType === "ETHEREUM") {
    const account = await getOrCreateEthereumAccount(key);

    // Match the Solana path: sign the message as a plain UTF-8 string
    // (EIP-191 personal_sign). viem returns a 0x-prefixed signature.
    signatureHex = await account.signMessage({ message: messageToSign });
  } else {
    TKHQ.sendMessageUp("ERROR", "unsupported message type", requestId);

    return;
  }

  TKHQ.sendMessageUp("MESSAGE_SIGNED", signatureHex, requestId);
}

/**
 * Function triggered when CLEAR_EMBEDDED_PRIVATE_KEY event is received.
 * @param {string} requestId
 * @param {string} address - Optional: The address of the key to clear (case-sensitive). If not provided, clears all keys.
 */
async function onClearEmbeddedPrivateKey(requestId, address) {
  // If no address is provided, clear all keys
  if (!address) {
    inMemoryKeys = {};
    TKHQ.sendMessageUp("EMBEDDED_PRIVATE_KEY_CLEARED", true, requestId);

    return;
  }

  // Check if key exists for the specific address
  if (!inMemoryKeys[address]) {
    TKHQ.sendMessageUp(
      "ERROR",
      new Error(
        `key not found for address ${address}. Note that address is case sensitive.`
      ).toString(),
      requestId
    );

    return;
  }

  // Clear the specific key from memory
  delete inMemoryKeys[address];

  TKHQ.sendMessageUp("EMBEDDED_PRIVATE_KEY_CLEARED", true, requestId);
}

/**
 * Handler for SET_EMBEDDED_KEY_OVERRIDE events.
 * Decrypts a P-256 private key bundle using the iframe's embedded key and
 * overrides the embedded key with it for subsequent bundle decryptions.
 * @param {string} requestId
 * @param {string} organizationId
 * @param {string} bundle - v1.0.0 bundle containing the P-256 private key
 * @param {Function} HpkeDecrypt
 */
async function onSetEmbeddedKeyOverride(
  requestId,
  organizationId,
  bundle,
  HpkeDecrypt
) {
  // Decrypt the private key using the iframe's embedded key.
  // The decrypted payload is a raw 32-byte P-256 private key scalar.
  const keyBytes = await decryptBundle(bundle, organizationId, HpkeDecrypt);

  // Convert raw P-256 bytes to a full JWK (derives public key via WebCrypto)
  const keyJwk = await rawP256PrivateKeyToJwk(new Uint8Array(keyBytes));

  // Store in module-level variable (memory only)
  injectedEmbeddedKey = keyJwk;

  TKHQ.sendMessageUp("EMBEDDED_KEY_OVERRIDE_SET", true, requestId);
}

/**
 * Handler for RESET_TO_DEFAULT_EMBEDDED_KEY events.
 * Clears the embedded key from memory, replacing it with the iframe's default embedded key.
 * @param {string} requestId
 */
function onResetToDefaultEmbeddedKey(requestId) {
  injectedEmbeddedKey = null;
  TKHQ.sendMessageUp("RESET_TO_DEFAULT_EMBEDDED_KEY", true, requestId);
}

// Utility functions
async function createSolanaKeypair(privateKey) {
  const privateKeyBytes = TKHQ.parsePrivateKey(privateKey);

  let keypair;
  if (privateKeyBytes.length === 32) {
    // 32-byte private key (seed)
    keypair = solanaKeypairFromSeed(privateKeyBytes);
  } else if (privateKeyBytes.length === 64) {
    // 64-byte secret key (private + public)
    keypair = solanaKeypairFromSecretKey(privateKeyBytes);
  } else {
    throw new Error(
      `Invalid private key length: ${privateKeyBytes.length}. Expected 32 or 64 bytes.`
    );
  }

  return keypair;
}

/**
 * Decodes a serialized Solana transaction (legacy, v0 or v1) and checks that
 * its message is well-formed before anything is signed.
 *
 * The transaction decoder only parses the signatures and the signer list; the
 * rest of the message is passed through as opaque bytes. So the message is
 * decoded in full and must re-encode to exactly the same bytes. This rejects
 * truncated messages, trailing bytes and non-canonical encodings, whose
 * signatures would not verify against the message a validator re-encodes.
 * @param {Uint8Array} transactionBytes
 * @returns {Object} - the decoded transaction ({ messageBytes, signatures })
 */
function decodeSolanaTransaction(transactionBytes) {
  let transaction;
  let message;
  let bytesRead;
  let reencodedMessage;
  try {
    transaction = getTransactionDecoder().decode(transactionBytes);
    [message, bytesRead] = getCompiledTransactionMessageDecoder().read(
      transaction.messageBytes,
      0
    );
    reencodedMessage = getCompiledTransactionMessageEncoder().encode(message);
  } catch (e) {
    // @solana/errors messages are opaque codes in production builds
    throw new Error("invalid Solana transaction: could not be decoded");
  }

  const { messageBytes } = transaction;
  if (
    bytesRead !== messageBytes.length ||
    reencodedMessage.length !== messageBytes.length ||
    reencodedMessage.some((byte, i) => byte !== messageBytes[i])
  ) {
    throw new Error(
      "invalid Solana transaction: message is not canonically encoded"
    );
  }

  // The transaction decoder reads signer addresses from where the message's
  // signer accounts should be, without checking that the message has them
  // (a v1 message can declare signers but no accounts). Signatures are also
  // keyed by address, so a duplicated signer collapses into one entry. So
  // the signers must be exactly the message's first numSignerAccounts static
  // accounts, in order.
  const signerAddresses = Object.keys(transaction.signatures);
  const messageSigners = message.staticAccounts.slice(
    0,
    message.header.numSignerAccounts
  );
  if (
    signerAddresses.length !== message.header.numSignerAccounts ||
    messageSigners.length !== message.header.numSignerAccounts ||
    signerAddresses.some((address, i) => address !== messageSigners[i])
  ) {
    throw new Error(
      "invalid Solana transaction: signers do not match the message's signer accounts"
    );
  }

  return transaction;
}

/**
 * Builds a Solana keypair from a 32-byte private key (seed).
 * @param {Uint8Array} seed
 * @returns {{secretKey: Uint8Array, publicKey: Uint8Array}} - secretKey is [seed][public key]
 */
function solanaKeypairFromSeed(seed) {
  const publicKey = nobleEd25519.getPublicKey(seed);
  const secretKey = new Uint8Array(64);
  secretKey.set(seed);
  secretKey.set(publicKey, 32);
  return { secretKey, publicKey };
}

/**
 * Builds a Solana keypair from a 64-byte secret key ([private key][public key]).
 * The public half must match the one derived from the private half, so a
 * corrupted key is rejected instead of producing invalid signatures.
 * @param {Uint8Array} secretKey
 * @returns {{secretKey: Uint8Array, publicKey: Uint8Array}}
 */
function solanaKeypairFromSecretKey(secretKey) {
  if (secretKey.length !== 64) {
    throw new Error(
      `invalid Solana secret key length: expected 64 bytes, got ${secretKey.length}`
    );
  }
  const publicKey = secretKey.slice(32);
  const derivedPublicKey = nobleEd25519.getPublicKey(secretKey.subarray(0, 32));
  if (derivedPublicKey.some((byte, i) => byte !== publicKey[i])) {
    throw new Error(
      "invalid Solana secret key: public key does not match private key"
    );
  }
  return { secretKey, publicKey };
}

/**
 * Converts raw P-256 private key bytes (32-byte scalar) to a JWK.
 * Constructs a PKCS8 wrapper around the raw bytes, imports via WebCrypto
 * (which derives the public key), then exports as JWK.
 * @param {Uint8Array} rawPrivateKeyBytes - 32-byte P-256 private key scalar
 * @returns {Promise<JsonWebKey>} - Full P-256 ECDH private key JWK
 */
async function rawP256PrivateKeyToJwk(rawPrivateKeyBytes) {
  if (rawPrivateKeyBytes.length !== 32) {
    throw new Error(
      `invalid decryption key length: expected 32 bytes, got ${rawPrivateKeyBytes.length}`
    );
  }

  // Fixed PKCS#8 DER prefix for a P-256 private key (36 bytes).
  // This wraps a raw 32-byte scalar into the PrivateKeyInfo structure
  // that WebCrypto's importKey("pkcs8", ...) expects.
  //
  // Structure (per RFC 5958 §2 / RFC 5208 §5):
  //   SEQUENCE {
  //     INTEGER 0                              -- version (v1)
  //     SEQUENCE {                             -- AlgorithmIdentifier (RFC 5480 §2.1.1)
  //       OID 1.2.840.10045.2.1               -- id-ecPublicKey
  //       OID 1.2.840.10045.3.1.7             -- secp256r1 (P-256)
  //     }
  //     OCTET STRING {                         -- privateKey (SEC 1 §C.4 / RFC 5915 §3)
  //       SEQUENCE {
  //         INTEGER 1                          -- version
  //         OCTET STRING (32 bytes)            -- raw private key scalar
  //       }
  //     }
  //   }
  //
  // References:
  //   - RFC 5958 / RFC 5208: PKCS#8 PrivateKeyInfo
  //   - RFC 5480 §2.1.1: ECC AlgorithmIdentifier (OIDs)
  //   - RFC 5915 / SEC 1 v2 §C.4: ECPrivateKey encoding
  const pkcs8Prefix = new Uint8Array([
    0x30, 0x41, 0x02, 0x01, 0x00, 0x30, 0x13, 0x06, 0x07, 0x2a, 0x86, 0x48,
    0xce, 0x3d, 0x02, 0x01, 0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03,
    0x01, 0x07, 0x04, 0x27, 0x30, 0x25, 0x02, 0x01, 0x01, 0x04, 0x20,
  ]);

  const pkcs8 = new Uint8Array(pkcs8Prefix.length + 32);
  pkcs8.set(pkcs8Prefix);
  pkcs8.set(rawPrivateKeyBytes, pkcs8Prefix.length);

  const cryptoKey = await crypto.subtle.importKey(
    "pkcs8",
    pkcs8,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits"]
  );

  return await crypto.subtle.exportKey("jwk", cryptoKey);
}

/**
 * Generates the error message for missing or expired keys.
 * @param {string} keyAddress - The address of the key
 * @returns {string} - The error message string
 */
export function getKeyNotFoundErrorMessage(keyAddress) {
  return `key bytes have expired. Please re-inject export bundle for address ${keyAddress} into iframe. Note that address is case sensitive.`;
}

/**
 * Clears an expired key from memory. This is an internal helper function
 * that clears the key without sending messages to the parent frame.
 * @param {string} keyAddress - The address of the key to clear
 */
function clearExpiredKey(keyAddress) {
  if (inMemoryKeys[keyAddress]) {
    delete inMemoryKeys[keyAddress];
  }
}

/**
 * Clears all expired keys from memory.
 * This function iterates through all keys and removes any that have expired.
 */
function clearAllExpiredKeys() {
  const now = new Date().getTime();
  const addressesToRemove = [];

  for (const [address, key] of Object.entries(inMemoryKeys)) {
    if (key.expiry && now >= key.expiry) {
      addressesToRemove.push(address);
    }
  }

  for (const address of addressesToRemove) {
    clearExpiredKey(address);
  }
}

/**
 * Validates that a key exists and has not expired.
 * Clears the key from memory if it has expired.
 * Throws error if validation fails (and caller will send message up back to parent).
 * @param {Object} key - The key object from inMemoryKeys
 * @param {string} keyAddress - The address of the key
 * @returns {boolean} - True if key is valid, false otherwise
 */
function validateKey(key, keyAddress) {
  if (!key) {
    throw new Error(
      `key bytes not found. Please re-inject export bundle for address ${keyAddress} into iframe. Note that address is case sensitive.`
    ).toString();
  }

  const now = new Date().getTime();
  if (now >= key.expiry) {
    // Clear all expired keys before processing the signing request
    clearAllExpiredKeys();
    throw new Error(getKeyNotFoundErrorMessage(keyAddress)).toString();
  }

  return true;
}

/**
 * Gets or creates a Solana keypair from a key object.
 * Uses cached keypair if available, otherwise creates a new one.
 * @param {Object} key - The key object containing format and privateKey
 * @returns {Promise<{secretKey: Uint8Array, publicKey: Uint8Array}>} - The Solana keypair
 */
async function getOrCreateKeypair(key) {
  if (key.keypair) {
    return key.keypair;
  }

  if (key.format === "SOLANA") {
    return solanaKeypairFromSecretKey(TKHQ.base58Decode(key.privateKey));
  } else {
    return await createSolanaKeypair(
      Array.from(TKHQ.uint8arrayFromHexString(key.privateKey))
    );
  }
}

/**
 * Gets or creates a viem Ethereum account from a key object.
 * Caches the account on the key object for improved signing perf.
 * @param {Object} key - The key object containing the privateKey and format
 * @returns {Promise<Object>} - The viem account (from privateKeyToAccount)
 */
async function getOrCreateEthereumAccount(key) {
  if (key.ethereumAccount) {
    return key.ethereumAccount;
  }

  // Ethereum keys are exported in HEXADECIMAL format, where privateKey is a
  // 0x-prefixed hex string — exactly what viem's privateKeyToAccount expects.
  if (key.format !== "HEXADECIMAL") {
    throw new Error(
      `cannot sign Ethereum payload with key format "${key.format}"; expected "HEXADECIMAL"`
    );
  }

  key.ethereumAccount = privateKeyToAccount(key.privateKey);
  return key.ethereumAccount;
}

/**
 * Signs a serialized Ethereum transaction and returns the broadcast-ready,
 * 0x-prefixed serialized signed transaction.
 *
 * Mirrors the Solana path: the caller builds and serializes the *unsigned*
 * transaction with any library, then passes the 0x-prefixed serialized hex.
 * We parse it back into viem's transaction shape, sign, and re-serialize.
 *
 * Note: for legacy (non-typed) transactions, the serialized unsigned tx must
 * include the chainId so the resulting signature carries EIP-155 replay
 * protection. Typed txs (EIP-1559/2930) always encode the chainId.
 * @param {Object} key - The key object containing the privateKey and format
 * @param {string} transactionToSign - 0x-prefixed serialized unsigned transaction
 * @returns {Promise<string>} - The 0x-prefixed serialized signed transaction
 */
async function signEthereumTransaction(key, transactionToSign) {
  const account = await getOrCreateEthereumAccount(key);
  return await account.signTransaction(parseTransaction(transactionToSign));
}

/**
 * DOM Event handlers to power the export flow in standalone mode
 * Instead of receiving events from the parent page, forms trigger them.
 * This is useful for debugging as well.
 */
function addDOMEventListeners() {
  // only support injected keys, not wallets
  document.getElementById("inject-key").addEventListener(
    "click",
    async (e) => {
      e.preventDefault();
      window.postMessage({
        type: "INJECT_KEY_EXPORT_BUNDLE",
        value: document.getElementById("key-export-bundle").value,
        keyFormat: document.getElementById("key-export-format").value,
        organizationId: document.getElementById("key-organization-id").value,
      });
    },
    false
  );
  document.getElementById("sign-transaction").addEventListener(
    "click",
    async (e) => {
      e.preventDefault();
      window.postMessage({
        type: "SIGN_TRANSACTION",
        value: document.getElementById("transaction-to-sign").value,
      });
    },
    false
  );
  document.getElementById("sign-message").addEventListener(
    "click",
    async (e) => {
      e.preventDefault();
      window.postMessage({
        type: "SIGN_MESSAGE",
        value: document.getElementById("message-to-sign").value,
      });
    },
    false
  );
  document.getElementById("reset").addEventListener(
    "click",
    async (e) => {
      e.preventDefault();
      window.postMessage({ type: "RESET_EMBEDDED_KEY" });
    },
    false
  );

  // Add wallet injection support
  const injectWalletBtn = document.getElementById("inject-wallet");
  if (injectWalletBtn) {
    injectWalletBtn.addEventListener(
      "click",
      async (e) => {
        e.preventDefault();
        window.postMessage({
          type: "INJECT_WALLET_EXPORT_BUNDLE",
          value: document.getElementById("wallet-export-bundle").value,
          organizationId: document.getElementById("wallet-organization-id")
            .value,
        });
      },
      false
    );
  }
}

/**
 * Message Event Handlers to process messages from the parent frame
 */
function initMessageEventListener(HpkeDecrypt) {
  return async function messageEventListener(event) {
    if (event.data && event.data["type"] == "INJECT_KEY_EXPORT_BUNDLE") {
      TKHQ.logMessage(
        `⬇️ Received message ${event.data["type"]}: ${event.data["value"]}, ${event.data["keyFormat"]}, ${event.data["organizationId"]}`
      );
      try {
        await onInjectKeyBundle(
          event.data["requestId"],
          event.data["organizationId"],
          event.data["value"], // bundle
          event.data["keyFormat"],
          event.data["address"],
          HpkeDecrypt
        );
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString(), event.data["requestId"]);
      }
    }
    if (event.data && event.data["type"] == "INJECT_WALLET_EXPORT_BUNDLE") {
      TKHQ.logMessage(
        `⬇️ Received message ${event.data["type"]}: ${event.data["value"]}, ${event.data["organizationId"]}`
      );
      try {
        await onInjectKeyBundle(
          event.data["requestId"],
          event.data["organizationId"],
          event.data["value"],
          undefined, // keyFormat - default to HEXADECIMAL
          undefined, // address - default to "default"
          HpkeDecrypt
        );
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString(), event.data["requestId"]);
      }
    }
    if (event.data && event.data["type"] == "APPLY_SETTINGS") {
      try {
        await onApplySettings(event.data["value"], event.data["requestId"]);
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString(), event.data["requestId"]);
      }
    }
    if (event.data && event.data["type"] == "RESET_EMBEDDED_KEY") {
      TKHQ.logMessage(`⬇️ Received message ${event.data["type"]}`);
      try {
        TKHQ.onResetEmbeddedKey();
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString());
      }
    }
    if (event.data && event.data["type"] == "SIGN_TRANSACTION") {
      TKHQ.logMessage(
        `⬇️ Received message ${event.data["type"]}: ${event.data["value"]}`
      );
      try {
        await onSignTransaction(
          event.data["requestId"],
          event.data["value"],
          event.data["address"] // signing address (case sensitive)
        );
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString(), event.data["requestId"]);
      }
    }
    if (event.data && event.data["type"] == "SIGN_MESSAGE") {
      TKHQ.logMessage(
        `⬇️ Received message ${event.data["type"]}: ${event.data["value"]}`
      );
      try {
        await onSignMessage(
          event.data["requestId"],
          event.data["value"],
          event.data["address"] // signing address (case sensitive)
        );
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString(), event.data["requestId"]);
      }
    }
    if (event.data && event.data["type"] == "CLEAR_EMBEDDED_PRIVATE_KEY") {
      TKHQ.logMessage(`⬇️ Received message ${event.data["type"]}`);
      try {
        await onClearEmbeddedPrivateKey(
          event.data["requestId"],
          event.data["address"]
        );
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString(), event.data["requestId"]);
      }
    }
    if (event.data && event.data["type"] == "GET_EMBEDDED_PUBLIC_KEY") {
      TKHQ.logMessage(`⬇️ Received message ${event.data["type"]}`);
      try {
        await onGetPublicEmbeddedKey(event.data["requestId"]);
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString(), event.data["requestId"]);
      }
    }
    if (event.data && event.data["type"] == "SET_EMBEDDED_KEY_OVERRIDE") {
      TKHQ.logMessage(`⬇️ Received message ${event.data["type"]}`);
      try {
        await onSetEmbeddedKeyOverride(
          event.data["requestId"],
          event.data["organizationId"],
          event.data["value"],
          HpkeDecrypt
        );
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString(), event.data["requestId"]);
      }
    }
    if (event.data && event.data["type"] == "RESET_TO_DEFAULT_EMBEDDED_KEY") {
      TKHQ.logMessage(`⬇️ Received message ${event.data["type"]}`);
      try {
        onResetToDefaultEmbeddedKey(event.data["requestId"]);
      } catch (e) {
        TKHQ.sendMessageUp("ERROR", e.toString(), event.data["requestId"]);
      }
    }
  };
}

/**
 * Set up event handlers for both DOM and message events
 * @param {Function} HpkeDecrypt
 */
export function initEventHandlers(HpkeDecrypt) {
  const messageEventListener = initMessageEventListener(HpkeDecrypt);

  // controllers to remove event listeners
  const messageListenerController = new AbortController();
  const turnkeyInitController = new AbortController();

  // Origin of a legacy (@turnkey/iframe-stamper < 2.1.0) parent, bound on its
  // first valid message. A document belongs to exactly one embedder for its
  // lifetime, so every subsequent message must come from the same origin.
  let legacyParentOrigin = null;
  let legacyTelemetryRecorded = false;

  if (window.parent === window) {
    // Standalone mode: the DOM forms drive the flow by posting messages to
    // this same window. Messages from other windows (e.g. a window.opener)
    // are not accepted.
    addDOMEventListeners();
    window.addEventListener(
      "message",
      async function (event) {
        if (event.source !== window) {
          return;
        }
        await messageEventListener(event);
      },
      {
        capture: false,
        signal: messageListenerController.signal,
      }
    );
    return { messageEventListener };
  }

  // Legacy embedded mode: @turnkey/iframe-stamper < 2.1.0 posts requests
  // straight to this window instead of establishing a MessageChannel.
  // These clients operate on the document-scoped ephemeral key (never
  // persisted -- see initEphemeralEmbeddedKey), so a malicious embedder can
  // only ever obtain a key that no legitimate bundle is encrypted to
  // (INT-697). Modern clients upgrade to the MessageChannel path below.
  window.addEventListener(
    "message",
    async function (event) {
      if (!event.data || !event.data["type"]) {
        return;
      }
      // Channel establishment is owned by the handshake listener below.
      if (event.data["type"] === "TURNKEY_INIT_MESSAGE_CHANNEL") {
        return;
      }
      // Only the direct parent, with a real (non-opaque) origin, may drive
      // the legacy path.
      if (event.source !== window.parent) {
        return;
      }
      if (!event.origin || event.origin === "null") {
        return;
      }
      if (legacyParentOrigin === null) {
        legacyParentOrigin = event.origin;
        // From now on, responses posted to window.parent are only readable
        // by the bound origin.
        TKHQ.setParentFrameOrigin(event.origin);
      } else if (event.origin !== legacyParentOrigin) {
        return;
      }
      if (!legacyTelemetryRecorded) {
        recordChannelTelemetry(CHANNEL_LEGACY_POST_MESSAGE, event.origin);
        legacyTelemetryRecorded = true;
      }
      await messageEventListener(event);
    },
    { capture: false, signal: messageListenerController.signal }
  );

  // Guard to prevent concurrent channel establishment from multiple senders
  let channelEstablished = false;

  // Handle MessageChannel initialization for iframe communication
  window.addEventListener(
    "message",
    async function (event) {
      /**
       * @turnkey/iframe-stamper >= v2.1.0 is using a MessageChannel to communicate with the parent frame.
       * The parent frame sends a TURNKEY_INIT_MESSAGE_CHANNEL event with the MessagePort.
       * If we receive this event, we want to remove the message event listener that was added in the DOMContentLoaded event to avoid processing messages twice.
       * We persist the MessagePort so we can use it to communicate with the parent window in subsequent calls to TKHQ.sendMessageUp
       */
      if (
        event.data &&
        event.data["type"] == "TURNKEY_INIT_MESSAGE_CHANNEL" &&
        event.source === window.parent &&
        event.origin &&
        event.origin !== "null" &&
        event.ports?.length === 1
      ) {
        // A legacy-bound document may upgrade to the MessageChannel protocol,
        // but never on behalf of a different origin.
        if (
          legacyParentOrigin !== null &&
          legacyParentOrigin !== event.origin
        ) {
          return;
        }

        // Synchronously check-and-set the flag before any await. This prevents
        // a second concurrent invocation from racing through while the first is
        // suspended at an await, which would allow multiple origins to establish
        // a channel before turnkeyInitController.abort() is reached.
        if (channelEstablished) {
          return;
        }
        channelEstablished = true;

        const iframeMessagePort = event.ports[0];

        var targetPubHex;
        try {
          // Supersedes the ephemeral key, if the legacy bootstrap created one.
          await TKHQ.initEmbeddedKey(event.origin);
          var embeddedKeyJwk = await TKHQ.getEmbeddedKey();
          var targetPubBuf = await TKHQ.p256JWKPrivateToPublic(embeddedKeyJwk);
          targetPubHex = TKHQ.uint8arrayToHexString(targetPubBuf);
        } catch (e) {
          // Key setup failed (e.g. blocked third-party storage). Roll back so
          // the parent can retry the handshake, and keep the legacy/standalone
          // listener alive so the frame stays functional in the meantime.
          channelEstablished = false;
          iframeMessagePort.postMessage({ type: "ERROR", value: e.toString() });
          return;
        }

        // Commit: the MessagePort carries all requests from here on; stop
        // accepting legacy/standalone window messages.
        messageListenerController.abort();
        TKHQ.setParentFrameMessageChannelPort(iframeMessagePort);
        document.getElementById("embedded-key").value = targetPubHex;

        iframeMessagePort.onmessage = messageEventListener;
        recordChannelTelemetry(CHANNEL_MESSAGE_CHANNEL, event.origin);
        TKHQ.sendMessageUp("PUBLIC_KEY_READY", targetPubHex);

        // remove the listener for TURNKEY_INIT_MESSAGE_CHANNEL after it's been processed
        turnkeyInitController.abort();
      }
    },
    { signal: turnkeyInitController.signal }
  );

  return { messageEventListener };
}
/**
 * Expose internal handlers for targeted testing.
 */
export {
  onInjectKeyBundle,
  onSignTransaction,
  onSignMessage,
  onClearEmbeddedPrivateKey,
  onSetEmbeddedKeyOverride,
  onResetToDefaultEmbeddedKey,
};
